import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBackNavigation } from '@/hooks/use-back-navigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Card, CardContent } from '@/components/ui/card';
import {
  Search,
  Plus,
  Trash2,
  Image,
  Building2,
  FileText,
  Hash,
  X,
  ArrowLeft,
  Loader2,
  Upload,
  Pencil,
} from 'lucide-react';
import { toast } from 'sonner';
import { authenticatedFetch, getBaseUrl } from '@/lib/api';
import { getAuthToken } from '@/lib/authToken';
import { useUserPrivileges } from '@/context/UserPrivilegesContext';
import { PRIVILEGE } from '@/lib/privilegeConstants';

// Company interface matching API response
export interface Company {
  Id: number;
  Name: string;
  LandPhone: string;
  Email: string;
  GSTN: string;
  Mobile: string;
  CreatedOn?: string;
  ContactPerson: string;
  ContactPersonNumber: string;
  Active?: boolean;
  LogoPath: string;
  BankAccountHolder: string | null;
  BankName: string | null;
  BankAccountNo: string | null;
  BankIFSC: string | null;
  BankAddress: string | null;
  GST: number;
  Logo: string;
  secondline: string | null;
  id1?: number;
  Address1: string;
  Address2: string;
  Address3: string;
  City: string;
  State: string;
  PinZip: string;
  Country?: string;
  id2?: number;
  CompanyID?: number;
  AddressID?: number;
  Active1?: boolean;
  StartDate?: string;
  EndDate?: string;
}

interface CompaniesResponse {
  status: number;
  message: string;
  data: Company[];
  TotalRecords?: number;
}

interface StatesResponse {
  status: number;
  message: string;
  data: Array<{ State: string; Code: string }>;
  TotalRecords?: number;
}

interface TermsNConditionsItem {
  Id: number;
  CompanyID: number;
  TermsNConditions: string;
  Type: string;
  Header: string;
}

interface GetTermsNConditionsResponse {
  status: number;
  message: string;
  data: TermsNConditionsItem[] | null;
  TotalRecords?: number;
}

interface SaveTermsNConditionsResponse {
  status: number;
  message: string;
  data: unknown;
  TotalRecords?: number;
}

interface CompanySequenceItem {
  id: number;
  CompanyID: number;
  MaxInvoiceNumber: number;
  MaxQuoteNumber: number;
  MaxDCNumber: number;
  InvoicePrefix: string;
  QuotePrefix: string;
  DCPrefix: string;
  CreditNoteNumber?: number | null;
  CreditNotePrefix?: string | null;
}

interface GetCompanySequenceResponse {
  status: number;
  message: string;
  data: CompanySequenceItem[];
  TotalRecords?: number;
}

const DOCUMENT_TYPES = [
  'Invoice',
  'Bill of Supply',
  'Credit Note',
  'Debit Note',
  'Quote',
  'Declaration',
  'PO',
];

/** Upload file to getWebURL API and return the image URL for LogoPath */
async function getImageUrlFromUpload(file: File): Promise<string> {
  console.log('[getWebURL] Uploading file:', file.name, 'size:', file.size);
  const formData = new FormData();
  formData.append('file', file);
  const baseUrl = getBaseUrl();
  const urlToCall = `${baseUrl}/api/getWebURL`;
  console.log('[getWebURL] POST', urlToCall);
  const res = await fetch(urlToCall, {
    method: 'POST',
    headers: { Authorization: getAuthToken() },
    body: formData,
  });
  const responseText = await res.text();
  let data: Record<string, unknown>;
  try {
    data = JSON.parse(responseText) as Record<string, unknown>;
  } catch {
    data = { _raw: responseText };
  }
  console.log('[getWebURL] Response status:', res.status, 'body:', data);
  if (!res.ok) throw new Error(`getWebURL failed: ${res.status}`);
  const url = (data?.url ?? (data?.data as Record<string, unknown>)?.url) as string | undefined;
  if (!url || url === '') throw new Error('API returned empty URL');
  return url;
}

const CompanyManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPrivilege } = useUserPrivileges();
  // Company tab is only shown when user has COMPANY_VIEW (checked at login); no need to re-check here for the list.
  const canCreateCompany = hasPrivilege(PRIVILEGE.COMPANY_CREATION);
  const canUpdateCompany = hasPrivilege(PRIVILEGE.COMPANY_UPDATE);
  const canDeleteCompany = hasPrivilege(PRIVILEGE.COMPANY_DELETE);
  const canInvoiceSequence = hasPrivilege(PRIVILEGE.INVOICE_SEQUENCE);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // States dropdown
  const [states, setStates] = useState<Array<{ State: string; Code: string }>>([]);
  const [statesLoading, setStatesLoading] = useState(false);

  // Modal states
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [companyFormMode, setCompanyFormMode] = useState<'add' | 'edit'>('add');
  const [editingCompanyId, setEditingCompanyId] = useState<number | null>(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [bankModalOpen, setBankModalOpen] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [sequenceModalOpen, setSequenceModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Add Company form
  const [addForm, setAddForm] = useState({
    Name: '',
    Mobile: '',
    Address1: '',
    PinZip: '',
    LandPhone: '',
    ContactPerson: '',
    Address2: '',
    State: 'Telangana',
    Email: '',
    GSTN: '',
    ContactPersonNumber: '',
    Address3: '',
    secondline: '',
    GST: false,
    City: '',
    Country: 'India',
  });
  const [logoPath, setLogoPath] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoUploadedUrl, setLogoUploadedUrl] = useState('');
  const [logoUploading, setLogoUploading] = useState(false);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addCompanyNameError, setAddCompanyNameError] = useState(false);

  // Image modal
  const [imageForm, setImageForm] = useState({ path: '', file: null as File | null });
  const [imageModalUploadedUrl, setImageModalUploadedUrl] = useState('');
  const [imageSubmitting, setImageSubmitting] = useState(false);

  // Bank details
  const [bankForm, setBankForm] = useState({
    AccountHolderName: '',
    BankName: '',
    IFSCCode: '',
    AccountNumber: '',
    BankAddress: '',
  });
  const [bankSubmitting, setBankSubmitting] = useState(false);

  // Terms & Conditions
  const [termsForm, setTermsForm] = useState({
    documentType: '',
    printName: '',
    content: '',
  });
  const [termsSubmitting, setTermsSubmitting] = useState(false);
  const [termsLoading, setTermsLoading] = useState(false);

  // Invoice / Quote / DC Sequence
  const [sequenceForm, setSequenceForm] = useState({
    maxInvoiceNumber: 0,
    maxQuoteNumber: 0,
    maxDCNumber: 0,
    invoicePrefix: '',
    quotePrefix: '',
    dcPrefix: '',
  });
  const [sequenceLoading, setSequenceLoading] = useState(false);
  const [sequenceSubmitting, setSequenceSubmitting] = useState(false);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const res = await authenticatedFetch<CompaniesResponse>('/api/Companies');
      const data = res.data as any;
      const list = Array.isArray(data) ? data : data?.data ?? [];
      setCompanies(list);
    } catch (err) {
      console.error('Failed to fetch companies:', err);
      toast.error('Failed to load companies');
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStates = async () => {
    try {
      setStatesLoading(true);
      const res = await authenticatedFetch<StatesResponse>('/api/States');
      const body = res.data as any;
      const list = Array.isArray(body) ? body : body?.data ?? [];
      setStates(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Failed to fetch states:', err);
      setStates([]);
    } finally {
      setStatesLoading(false);
    }
  };

  // Fetch companies and states on mount. Company tab is only visible when user has Company View (27) at login, so no need to re-check privilege here.
  useEffect(() => {
    fetchCompanies();
    fetchStates();
  }, []);

  const filteredCompanies = useMemo(() => {
    if (!searchQuery.trim()) return companies;
    const q = searchQuery.toLowerCase();
    return companies.filter(
      (c) =>
        c.Name?.toLowerCase().includes(q) ||
        c.Email?.toLowerCase().includes(q) ||
        c.GSTN?.toLowerCase().includes(q) ||
        c.Mobile?.includes(q) ||
        c.ContactPerson?.toLowerCase().includes(q) ||
        c.City?.toLowerCase().includes(q) ||
        c.State?.toLowerCase().includes(q)
    );
  }, [companies, searchQuery]);

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      if (prev.has(id)) return new Set<number>();
      return new Set([id]);
    });
  };

  const handleClose = () => navigate('/?tab=administrator');

  // Back button (hardware/browser) should return to Administrator tab, not Reports
  useBackNavigation({
    onBack: handleClose,
    showExitConfirmation: false,
  });

  const handleAddCompany = async () => {
    if (!addForm.Name?.trim()) {
      setAddCompanyNameError(true);
      toast.error('Company name is required');
      return;
    }
    setAddCompanyNameError(false);
    // getweburl is called only when Browse is clicked; we use the stored URL here.
    if (logoFile && logoUploading) {
      toast.error('Please wait for logo upload to complete.');
      return;
    }
    if (logoFile && !logoUploadedUrl) {
      toast.error('Logo upload failed or incomplete. Please select the logo again.');
      return;
    }

    try {
      setAddSubmitting(true);
      // When user selected a file we already have logoUploadedUrl from getweburl (called on Browse). Send it in Company POST and also in CompanyLogo after company is created.
      let logoPathValue = '';
      const hasLogoFile = !!logoFile;

      if (logoFile && logoUploadedUrl) {
        logoPathValue = logoUploadedUrl;
      } else if (!logoFile) {
        logoPathValue = logoPath || '';
      }

      const payload = {
        Name: addForm.Name,
        SecondLine: addForm.secondline || '',
        LandPhone: addForm.LandPhone || '',
        Email: addForm.Email || '',
        GSTN: addForm.GSTN || '',
        Mobile: addForm.Mobile || '',
        ContactPerson: addForm.ContactPerson || '',
        ContactPersonNumber: addForm.ContactPersonNumber || '',
        IsGST: addForm.GST,
        LogoPath: logoPathValue || logoPath || '',
        Address1: addForm.Address1 || '',
        Address2: addForm.Address2 || '',
        Address3: addForm.Address3 || '',
        City: addForm.City || '',
        State: addForm.State || '',
        PinZip: addForm.PinZip || '',
        Country: 'India',
      };

      console.log('[Company POST] Request JSON:', JSON.stringify(payload, null, 2));

      const res = await authenticatedFetch<{ status?: number; message?: string; CompanyId?: number; AddressId?: number; data?: any }>('/api/Company', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      console.log('[Company POST] Response:', res);

      const apiData = res.data as Record<string, unknown>;
      const status = (apiData?.status as number) ?? res.status;
      // Extract CompanyId from various response shapes: root, data object, or data[0]
      let newCompanyId: number | undefined;
      if (typeof (apiData?.CompanyId ?? (apiData as any)?.companyId) === 'number') {
        newCompanyId = (apiData?.CompanyId ?? (apiData as any)?.companyId) as number;
      } else if (apiData?.data != null) {
        const d = apiData.data as Record<string, unknown>;
        if (typeof (d?.CompanyId ?? (d as any)?.companyId) === 'number') {
          newCompanyId = (d?.CompanyId ?? (d as any)?.companyId) as number;
        } else if (Array.isArray(d) && d[0] != null && typeof (d[0] as any)?.CompanyId === 'number') {
          newCompanyId = (d[0] as any).CompanyId as number;
        } else if (typeof d === 'number') {
          newCompanyId = d;
        }
      }
      console.log('[Company POST] extracted CompanyId:', newCompanyId);

      if (status === 0) {
        const hasLogoUrl = logoPathValue && String(logoPathValue).startsWith('http');
        if (hasLogoFile && typeof newCompanyId === 'number' && hasLogoUrl) {
          const logoPayload = { Id: newCompanyId, LogoPath: logoPathValue };
          console.log('[CompanyLogo] POST api/CompanyLogo body:', JSON.stringify(logoPayload, null, 2));
          try {
            const logoRes = await authenticatedFetch<{ status?: number; message?: string; data?: unknown }>('/api/CompanyLogo', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(logoPayload),
            });
            console.log('[CompanyLogo] Response:', logoRes);
            const logoStatus = (logoRes.data as Record<string, unknown>)?.status;
            if (logoStatus === -1) {
              const logoMsg = (logoRes.data as Record<string, unknown>)?.message ?? 'Logo update failed';
              toast.warning(String(logoMsg));
            }
          } catch (e) {
            console.warn('[CompanyLogo] Failed (company already created):', e);
            toast.warning('Company created but logo could not be updated. You can set it via Add/Modify Image.');
          }
        }
        toast.success('Company created successfully');
        setAddModalOpen(false);
        resetAddForm();
        fetchCompanies();
      } else if (status === -1) {
        const errMsg = apiData?.message || (res as any).message || 'Failed to create company';
        toast.error(errMsg);
      } else {
        toast.success('Company created successfully');
        setAddModalOpen(false);
        resetAddForm();
        fetchCompanies();
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to add company';
      toast.error(msg);
    } finally {
      setAddSubmitting(false);
    }
  };

  const resetAddForm = () => {
    setAddCompanyNameError(false);
    setAddForm({
      Name: '',
      Mobile: '',
      Address1: '',
      PinZip: '',
      LandPhone: '',
      ContactPerson: '',
      Address2: '',
      State: 'Telangana',
      Email: '',
      GSTN: '',
      ContactPersonNumber: '',
      Address3: '',
      secondline: '',
      GST: false,
      City: '',
      Country: 'India',
    });
    setLogoPath('');
    setLogoFile(null);
    setLogoUploadedUrl('');
    setLogoUploading(false);
  };

  const openAddModal = () => {
    setCompanyFormMode('add');
    setEditingCompanyId(null);
    resetAddForm();
    setAddModalOpen(true);
  };

  const openUpdateModal = () => {
    if (selectedIds.size !== 1) return;
    const company = companies.find((c) => c.Id === [...selectedIds][0]);
    if (!company) return;
    setAddCompanyNameError(false);
    setCompanyFormMode('edit');
    setEditingCompanyId(company.Id);
    setAddForm({
      Name: company.Name || '',
      Mobile: company.Mobile || '',
      Address1: company.Address1 || '',
      PinZip: company.PinZip || '',
      LandPhone: company.LandPhone || '',
      ContactPerson: company.ContactPerson || '',
      Address2: company.Address2 || '',
      State: company.State || '',
      Email: company.Email || '',
      GSTN: company.GSTN || '',
      ContactPersonNumber: company.ContactPersonNumber || '',
      Address3: company.Address3 || '',
      secondline: company.secondline || '',
      GST: !!company.GST,
      City: company.City || '',
      Country: 'India',
    });
    setAddModalOpen(true);
  };

  const handleUpdateCompany = async () => {
    if (editingCompanyId == null) return;
    if (!addForm.Name?.trim()) {
      setAddCompanyNameError(true);
      toast.error('Company name is required');
      return;
    }
    setAddCompanyNameError(false);
    try {
      setAddSubmitting(true);
      const payload = {
        Id: editingCompanyId,
        Name: addForm.Name,
        LandPhone: addForm.LandPhone || '',
        Email: addForm.Email || '',
        GSTN: addForm.GSTN || '',
        Mobile: addForm.Mobile || '',
        ContactPerson: addForm.ContactPerson || '',
        ContactPersonNumber: addForm.ContactPersonNumber || '',
        IsGST: addForm.GST,
        Address1: addForm.Address1 || '',
        Address2: addForm.Address2 || '',
        Address3: addForm.Address3 || '',
        City: addForm.City || '',
        State: addForm.State || '',
        PinZip: addForm.PinZip || '',
      };
      console.log('[Company Update] Request JSON:', JSON.stringify(payload, null, 2));
      const res = await authenticatedFetch<{ status?: number; message?: string; data?: any }>('/api/UpdateCompany', {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      console.log('[Company Update] Response:', res);
      const apiData = res.data as { status?: number; message?: string };
      const status = apiData?.status ?? res.status;
      if (status === 0) {
        toast.success('Company updated successfully');
        setAddModalOpen(false);
        setEditingCompanyId(null);
        fetchCompanies();
      } else if (status === -1) {
        const errMsg = apiData?.message || (res as any).message || 'Failed to update company';
        toast.error(errMsg);
      } else {
        toast.success('Company updated successfully');
        setAddModalOpen(false);
        setEditingCompanyId(null);
        fetchCompanies();
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to update company';
      toast.error(msg);
    } finally {
      setAddSubmitting(false);
    }
  };

  const handleDeleteClick = () => {
    if (selectedIds.size === 0) {
      toast.error('Select at least one company to delete');
      return;
    }
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      let allSuccess = true;
      for (const id of selectedIds) {
        const res = await authenticatedFetch<{ status?: number; message?: string }>('/api/DeleteCompany', {
          method: 'DELETE',
          body: JSON.stringify({ Id: id }),
        });
        const apiData = res.data as { status?: number; message?: string };
        const status = apiData?.status ?? res.status;
        if (status === -1) {
          toast.error(apiData?.message || 'Failed to delete company');
          allSuccess = false;
        }
      }
      if (allSuccess) {
        toast.success('Company deleted successfully');
        setSelectedIds(new Set());
        setDeleteConfirmOpen(false);
        fetchCompanies();
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete company';
      toast.error(msg);
    }
  };

  const openImageModal = () => {
    const company = selectedIds.size === 1 ? companies.find((c) => c.Id === [...selectedIds][0]) : null;
    setSelectedCompany(company ?? null);
    setImageForm({ path: company?.LogoPath || company?.Logo || '', file: null });
    setImageModalUploadedUrl('');
    setImageModalOpen(true);
  };

  const handleImageUpload = async () => {
    if (!selectedCompany?.Id) {
      toast.error('No company selected');
      return;
    }
    try {
      setImageSubmitting(true);
      let logoUrl: string;
      if (imageModalUploadedUrl) {
        logoUrl = imageModalUploadedUrl;
      } else if (imageForm.file) {
        logoUrl = await getImageUrlFromUpload(imageForm.file);
      } else if (imageForm.path?.trim()) {
        logoUrl = imageForm.path.trim();
      } else {
        toast.error('Please select an image file or enter a logo URL');
        return;
      }

      const payload = { Id: selectedCompany.Id, LogoPath: logoUrl };
      console.log('[CompanyLogo] Request JSON:', JSON.stringify(payload, null, 2));

      const res = await authenticatedFetch<{ status?: number; message?: string; data?: unknown }>('/api/CompanyLogo', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      console.log('[CompanyLogo] Response:', res);

      const apiData = res.data as { status?: number; message?: string };
      const status = apiData?.status ?? (res as { status?: number }).status;

      if (status === 0) {
        toast.success('Logo updated successfully');
        setImageModalOpen(false);
        fetchCompanies();
      } else if (status === -1) {
        const errMsg = apiData?.message ?? (res as { message?: string }).message ?? 'Failed to update logo';
        toast.error(errMsg);
      } else {
        toast.success('Logo updated successfully');
        setImageModalOpen(false);
        fetchCompanies();
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to update logo';
      toast.error(msg);
    } finally {
      setImageSubmitting(false);
    }
  };

  const openBankModal = () => {
    const company = selectedIds.size === 1 ? companies.find((c) => c.Id === [...selectedIds][0]) : null;
    setSelectedCompany(company ?? null);
    setBankForm({
      AccountHolderName: company?.BankAccountHolder || '',
      BankName: company?.BankName || '',
      IFSCCode: company?.BankIFSC || '',
      AccountNumber: company?.BankAccountNo || '',
      BankAddress: company?.BankAddress || '',
    });
    setBankModalOpen(true);
  };

  const handleBankSave = async () => {
    if (!selectedCompany?.Id) {
      toast.error('No company selected');
      return;
    }
    try {
      setBankSubmitting(true);
      const payload = {
        CompanyId: selectedCompany.Id,
        BankAccountHolder: bankForm.AccountHolderName || '',
        BankName: bankForm.BankName || '',
        BankAccountNo: bankForm.AccountNumber || '',
        BankIFSC: bankForm.IFSCCode || '',
        BankAddress: bankForm.BankAddress || '',
      };
      console.log('[CompanyBankDetails] Request JSON:', JSON.stringify(payload, null, 2));

      const res = await authenticatedFetch<{ status?: number; message?: string; data?: unknown }>('/api/CompanyBankDetails', {
        method: 'PUT',
        body: JSON.stringify(payload),
      });

      console.log('[CompanyBankDetails] Response:', res);

      const apiData = res.data as { status?: number; message?: string };
      const status = apiData?.status ?? (res as { status?: number }).status;

      if (status === 0) {
        toast.success('Bank details saved successfully');
        setBankModalOpen(false);
        fetchCompanies();
      } else if (status === -1) {
        const errMsg = apiData?.message ?? (res as { message?: string }).message ?? 'Failed to save bank details';
        toast.error(errMsg);
      } else {
        toast.success('Bank details saved successfully');
        setBankModalOpen(false);
        fetchCompanies();
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save bank details';
      toast.error(msg);
    } finally {
      setBankSubmitting(false);
    }
  };

  const fetchTermsForType = async (companyId: number, type: string) => {
    if (!type) return;
    try {
      setTermsLoading(true);
      const res = await authenticatedFetch<GetTermsNConditionsResponse>(
        `/api/GetTermsNConditions?CompanyId=${companyId}&type=${encodeURIComponent(type)}`
      );
      const body = res.data as GetTermsNConditionsResponse;
      const list = Array.isArray(body?.data) ? body.data : [];
      const first = list[0];
      setTermsForm((prev) => ({
        ...prev,
        documentType: type,
        printName: first?.Header ?? '',
        content: first?.TermsNConditions ?? '',
      }));
    } catch (err) {
      console.error('Failed to fetch terms:', err);
      toast.error('Failed to load terms & conditions');
      setTermsForm((prev) => ({ ...prev, documentType: type, printName: '', content: '' }));
    } finally {
      setTermsLoading(false);
    }
  };

  const openTermsModal = () => {
    const company = selectedIds.size === 1 ? companies.find((c) => c.Id === [...selectedIds][0]) : null;
    setSelectedCompany(company ?? null);
    setTermsForm({ documentType: 'Invoice', printName: '', content: '' });
    setTermsModalOpen(true);
    if (company?.Id) {
      fetchTermsForType(company.Id, 'Invoice');
    }
  };

  const handleTermsDocumentTypeChange = (type: string) => {
    setTermsForm((prev) => ({ ...prev, documentType: type, printName: '', content: '' }));
    if (selectedCompany?.Id && type) {
      fetchTermsForType(selectedCompany.Id, type);
    }
  };

  const handleTermsSave = async () => {
    if (!selectedCompany?.Id) {
      toast.error('No company selected');
      return;
    }
    if (!termsForm.documentType) {
      toast.error('Please select a document type');
      return;
    }
    try {
      setTermsSubmitting(true);
      const payload = {
        companyId: selectedCompany.Id,
        type: termsForm.documentType,
        termsNConditions: termsForm.content || '',
        header: termsForm.printName || '',
      };
      const res = await authenticatedFetch<SaveTermsNConditionsResponse>('/api/SaveTermsNConditions', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const body = res.data as SaveTermsNConditionsResponse;
      const status = body?.status ?? res.status;
      if (status === 0) {
        toast.success(body?.message ?? 'Saved successfully');
        setTermsModalOpen(false);
      } else if (status === -1) {
        toast.error(body?.message ?? 'Failed to save terms & conditions');
      } else {
        toast.success(body?.message ?? 'Saved successfully');
        setTermsModalOpen(false);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save terms & conditions';
      toast.error(msg);
    } finally {
      setTermsSubmitting(false);
    }
  };

  const fetchCompanySequence = async (companyId: number) => {
    const getUrl = `/api/GetCompanySequence?CompanyId=${companyId}`;
    try {
      setSequenceLoading(true);
      console.log('[GetCompanySequence] API:', getUrl);
      const res = await authenticatedFetch<GetCompanySequenceResponse>(getUrl);
      const body = res.data as GetCompanySequenceResponse;
      console.log('[GetCompanySequence] Response JSON:', JSON.stringify(res, null, 2));
      const list = Array.isArray(body?.data) ? body.data : [];
      const first = list[0];
      if (first) {
        setSequenceForm({
          maxInvoiceNumber: Number(first.MaxInvoiceNumber) || 0,
          maxQuoteNumber: Number(first.MaxQuoteNumber) || 0,
          maxDCNumber: Number(first.MaxDCNumber) || 0,
          invoicePrefix: first.InvoicePrefix ?? '',
          quotePrefix: first.QuotePrefix ?? '',
          dcPrefix: first.DCPrefix ?? '',
        });
      } else {
        setSequenceForm({
          maxInvoiceNumber: 0,
          maxQuoteNumber: 0,
          maxDCNumber: 0,
          invoicePrefix: '',
          quotePrefix: '',
          dcPrefix: '',
        });
      }
    } catch (err) {
      console.error('Failed to fetch company sequence:', err);
      toast.error('Failed to load sequence');
      setSequenceForm({
        maxInvoiceNumber: 0,
        maxQuoteNumber: 0,
        maxDCNumber: 0,
        invoicePrefix: '',
        quotePrefix: '',
        dcPrefix: '',
      });
    } finally {
      setSequenceLoading(false);
    }
  };

  const openSequenceModal = () => {
    if (selectedIds.size !== 1) return;
    const company = companies.find((c) => c.Id === [...selectedIds][0]);
    if (!company) return;
    setSelectedCompany(company);
    setSequenceModalOpen(true);
    fetchCompanySequence(company.Id);
  };

  const handleSequenceSave = async () => {
    if (!selectedCompany?.Id) {
      toast.error('No company selected');
      return;
    }
    try {
      setSequenceSubmitting(true);
      const payload = {
        CompanyId: selectedCompany.Id,
        MaxInvoiceNumber: Number(sequenceForm.maxInvoiceNumber) || 0,
        MaxQuoteNumber: Number(sequenceForm.maxQuoteNumber) || 0,
        MaxDCNumber: Number(sequenceForm.maxDCNumber) || 0,
        InvoicePrefix: sequenceForm.invoicePrefix ?? '',
        QuotePrefix: sequenceForm.quotePrefix ?? '',
        DCPrefix: sequenceForm.dcPrefix ?? '',
      };
      const postUrl = '/api/SaveCompanySequence';
      console.log('[SaveCompanySequence] API:', postUrl);
      console.log('[SaveCompanySequence] Request JSON:', JSON.stringify(payload, null, 2));
      const res = await authenticatedFetch<{ status?: number; message?: string }>(postUrl, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const body = res.data as { status?: number; message?: string };
      console.log('[SaveCompanySequence] Response JSON:', JSON.stringify(res, null, 2));
      const status = body?.status ?? (res as { status?: number }).status;
      if (status === 0) {
        toast.success(body?.message ?? 'Sequence saved successfully');
        setSequenceModalOpen(false);
      } else if (status === -1) {
        toast.error(body?.message ?? 'Failed to save sequence');
      } else {
        toast.success(body?.message ?? 'Sequence saved successfully');
        setSequenceModalOpen(false);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save sequence';
      toast.error(msg);
    } finally {
      setSequenceSubmitting(false);
    }
  };

  const ActionBtn = ({
    icon: Icon,
    label,
    onClick,
    disabled,
    variant = 'default' as const,
  }: {
    icon: React.ElementType;
    label: string;
    onClick: () => void;
    disabled?: boolean;
    variant?: 'default' | 'outline' | 'destructive' | 'secondary';
  }) => (
    <Button
      variant={variant}
      size="sm"
      className="transition-all duration-200 hover:scale-105 hover:shadow-md shrink-0"
      onClick={onClick}
      disabled={disabled}
    >
      <Icon className="h-4 w-4 md:mr-2" />
      <span className="hidden md:inline">{label}</span>
    </Button>
  );

  return (
    <div className="min-h-screen bg-gray-50/80 p-4 md:p-6 animate-in fade-in duration-300">
      <div className="max-w-7xl mx-auto space-y-4 md:space-y-6">
        {/* Header with Back */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-foreground"
            onClick={handleClose}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Administrator
          </Button>
          <h2 className="text-lg font-semibold text-gray-900">Company Management</h2>
        </div>

        {/* Top Section */}
        <div className="flex flex-col gap-4">
          {/* Desktop: Search + buttons in one row */}
          <div className="hidden md:flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[200px] max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search companies..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 rounded-lg border-gray-200 bg-white shadow-sm"
                />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {canCreateCompany && (
              <ActionBtn icon={Plus} label="Add Company" onClick={openAddModal} />
              )}
              {canUpdateCompany && (
              <ActionBtn
                icon={Pencil}
                label="Update"
                variant="outline"
                onClick={openUpdateModal}
                disabled={selectedIds.size !== 1}
              />
              )}
              {canDeleteCompany && (
              <ActionBtn
                icon={Trash2}
                label="Delete"
                variant="destructive"
                onClick={handleDeleteClick}
                disabled={selectedIds.size === 0}
              />
              )}
              <ActionBtn
                icon={Image}
                label="Add/Modify Image"
                variant="outline"
                onClick={openImageModal}
                disabled={selectedIds.size !== 1}
              />
              <ActionBtn
                icon={Building2}
                label="Bank Details"
                variant="outline"
                onClick={openBankModal}
                disabled={selectedIds.size !== 1}
              />
              <ActionBtn
                icon={FileText}
                label="Terms & Conditions"
                variant="outline"
                onClick={openTermsModal}
                disabled={selectedIds.size !== 1}
              />
              {canInvoiceSequence && (
              <ActionBtn icon={Hash} label="Invoice Sequence" variant="outline" onClick={openSequenceModal} disabled={selectedIds.size !== 1} />
              )}
            </div>
          </div>

          {/* Mobile: Action buttons scroll row */}
          <div className="md:hidden overflow-x-auto pb-2 -mx-4 px-4">
            <div className="flex gap-2 min-w-max">
              {canDeleteCompany && <ActionBtn icon={Trash2} label="Delete" variant="destructive" onClick={handleDeleteClick} disabled={selectedIds.size === 0} />}
              {canUpdateCompany && <ActionBtn icon={Pencil} label="Update" variant="outline" onClick={openUpdateModal} disabled={selectedIds.size !== 1} />}
              <ActionBtn icon={Image} label="Image" variant="outline" onClick={openImageModal} disabled={selectedIds.size !== 1} />
              <ActionBtn icon={Building2} label="Bank" variant="outline" onClick={openBankModal} disabled={selectedIds.size !== 1} />
              <ActionBtn icon={FileText} label="T&C" variant="outline" onClick={openTermsModal} disabled={selectedIds.size !== 1} />
              {canInvoiceSequence && <ActionBtn icon={Hash} label="Invoice Seq" variant="outline" onClick={openSequenceModal} disabled={selectedIds.size !== 1} />}
            </div>
          </div>

          {/* Mobile: Search + Add */}
          <div className="md:hidden flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search companies..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            {canCreateCompany && (
            <Button className="shrink-0" onClick={openAddModal}>
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
            )}
          </div>
        </div>

        {/* Company List */}
        <Card className="border-gray-100 shadow-sm overflow-hidden">
          <ScrollArea className="h-[calc(100vh-280px)] md:h-[400px]">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              </div>
            ) : filteredCompanies.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                <Building2 className="h-12 w-12 mb-2 opacity-50" />
                <p>No companies found</p>
              </div>
            ) : (
              <>
                {/* Desktop Table */}
                <Table className="hidden md:table">
                  <TableHeader>
                    <TableRow className="bg-gray-50 hover:bg-gray-50">
                      <TableHead className="w-10" />
                      <TableHead>Name</TableHead>
                      <TableHead>LandLine</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>GSTN</TableHead>
                      <TableHead>Mobile</TableHead>
                      <TableHead>Contact Person</TableHead>
                      <TableHead>Contact No</TableHead>
                      <TableHead>HNO</TableHead>
                      <TableHead>Street</TableHead>
                      <TableHead>Extra Address</TableHead>
                      <TableHead>City</TableHead>
                      <TableHead>State</TableHead>
                      <TableHead>Pin</TableHead>
                      <TableHead>GST</TableHead>
                      <TableHead>Second Line</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCompanies.map((c, i) => (
                      <TableRow
                        key={c.Id}
                        className={`transition-colors cursor-pointer ${i % 2 === 1 ? 'bg-gray-50/50' : ''} hover:bg-blue-50/50`}
                        onClick={() => toggleSelect(c.Id)}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox checked={selectedIds.has(c.Id)} onCheckedChange={() => toggleSelect(c.Id)} />
                        </TableCell>
                        <TableCell className="font-medium">{c.Name || '-'}</TableCell>
                        <TableCell>{c.LandPhone || '-'}</TableCell>
                        <TableCell>{c.Email || '-'}</TableCell>
                        <TableCell>{c.GSTN || '-'}</TableCell>
                        <TableCell>{c.Mobile || '-'}</TableCell>
                        <TableCell>{c.ContactPerson || '-'}</TableCell>
                        <TableCell>{c.ContactPersonNumber || '-'}</TableCell>
                        <TableCell>{c.Address1 || '-'}</TableCell>
                        <TableCell>{c.Address2 || '-'}</TableCell>
                        <TableCell>{c.Address3 || '-'}</TableCell>
                        <TableCell>{c.City || '-'}</TableCell>
                        <TableCell>{c.State || '-'}</TableCell>
                        <TableCell>{c.PinZip || '-'}</TableCell>
                        <TableCell>{c.GST ? 'Yes' : 'No'}</TableCell>
                        <TableCell>{c.secondline || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Mobile Cards */}
                <div className="md:hidden divide-y divide-gray-100 p-4 space-y-4">
                  {filteredCompanies.map((c, i) => (
                    <Card
                      key={c.Id}
                      className={`p-4 shadow-sm animate-in slide-in-from-bottom-2 duration-300 bg-white border-gray-100`}
                      style={{ animationDelay: `${i * 30}ms` }}
                    >
                      <div className="flex gap-3">
                        <div
                          className="flex-shrink-0 flex items-start pt-0.5"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            checked={selectedIds.has(c.Id)}
                            onCheckedChange={() => toggleSelect(c.Id)}
                            className="h-6 w-6 rounded border-2 border-gray-300 data-[state=checked]:border-primary"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 truncate">{c.Name || 'Unnamed'}</p>
                          <p className="text-sm text-muted-foreground mt-1">{c.Mobile || '-'}</p>
                          <p className="text-sm text-muted-foreground">{c.Email || '-'}</p>
                          <p className="text-sm text-muted-foreground">{c.GSTN || '-'}</p>
                          <p className="text-sm mt-2">
                            {[c.City, c.State].filter(Boolean).join(', ') || '-'}
                          </p>
                          <p className="text-sm text-muted-foreground">Contact: {c.ContactPerson || '-'}</p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </>
            )}
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </Card>

        {/* Bottom: Close */}
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="lg"
            className="rounded-lg shadow-sm hover:shadow transition-all"
            onClick={handleClose}
          >
            <X className="h-4 w-4 mr-2" />
            Close
          </Button>
        </div>
      </div>

      {/* Add / Update Company Modal */}
      <Dialog open={addModalOpen} onOpenChange={(open) => { setAddModalOpen(open); if (!open) setEditingCompanyId(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {companyFormMode === 'edit' ? <Pencil className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
              {companyFormMode === 'edit' ? 'Update Company' : 'Add Company'}
            </DialogTitle>
            <DialogDescription>
              {companyFormMode === 'edit' ? 'Update the company details below.' : 'Fill in the company details below.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            {(
              companyFormMode === 'edit'
                ? [
                    { key: 'Name', label: 'Company Name' },
                    { key: 'LandPhone', label: 'LandLine' },
                    { key: 'Email', label: 'Email' },
                    { key: 'GSTN', label: 'GSTN' },
                    { key: 'Mobile', label: 'Mobile' },
                    { key: 'ContactPerson', label: 'Contact Person Name' },
                    { key: 'ContactPersonNumber', label: 'Contact Person Mobile' },
                    { key: 'Address1', label: 'H.NO' },
                    { key: 'Address2', label: 'Street Details' },
                    { key: 'Address3', label: 'Extra Address Line' },
                    { key: 'City', label: 'City' },
                    { key: 'State', label: 'State' },
                    { key: 'PinZip', label: 'Pincode (Zip)' },
                  ]
                : [
                    { key: 'Name', label: 'Company Name' },
                    { key: 'Mobile', label: 'Mobile' },
                    { key: 'Address1', label: 'H.NO' },
                    { key: 'PinZip', label: 'Pincode (Zip)' },
                    { key: 'LandPhone', label: 'LandLine' },
                    { key: 'ContactPerson', label: 'Contact Person Name' },
                    { key: 'Address2', label: 'Street Details' },
                    { key: 'State', label: 'State' },
                    { key: 'Email', label: 'Email' },
                    { key: 'GSTN', label: 'GSTN' },
                    { key: 'ContactPersonNumber', label: 'Contact Person Mobile' },
                    { key: 'Address3', label: 'Extra Address Line' },
                    { key: 'secondline', label: 'Second Line' },
                    { key: 'City', label: 'City' },
                  ]
            ).map(({ key, label }) => {
              const isMobileField = key === 'Mobile' || key === 'ContactPersonNumber';
              const isNameRequired = key === 'Name';
              const showNameError = isNameRequired && addCompanyNameError;
              return (
                <div key={key} className="space-y-2">
                  <Label>
                    {label}
                    {isNameRequired && <span className="text-red-500 ml-0.5">*</span>}
                  </Label>
                  {key === 'State' ? (
                    <Select
                      value={addForm.State || ''}
                      onValueChange={(v) => setAddForm((p) => ({ ...p, State: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={statesLoading ? 'Loading states...' : 'Select state'} />
                      </SelectTrigger>
                      <SelectContent>
                        {states.map((s) => (
                          <SelectItem key={`${s.Code}-${s.State}`} value={s.State}>
                            {s.State}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <>
                      <Input
                        value={(addForm as any)[key] || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (isNameRequired) setAddCompanyNameError(false);
                          if (isMobileField) {
                            const digitsOnly = value.replace(/\D/g, '');
                            if (digitsOnly.length <= 10) {
                              setAddForm((p) => ({ ...p, [key]: digitsOnly }));
                            }
                          } else {
                            setAddForm((p) => ({ ...p, [key]: value }));
                          }
                        }}
                        placeholder={label}
                        inputMode={isMobileField ? 'numeric' : 'text'}
                        maxLength={isMobileField ? 10 : undefined}
                        readOnly={key === 'Name' && companyFormMode === 'edit'}
                        className={`${showNameError ? 'border-red-500 focus-visible:ring-red-500' : ''} ${key === 'Name' && companyFormMode === 'edit' ? 'bg-muted cursor-not-allowed' : ''}`}
                        aria-invalid={showNameError}
                        aria-describedby={showNameError ? 'company-name-error' : undefined}
                      />
                      {showNameError && (
                        <p id="company-name-error" className="text-sm text-red-500">
                          Company name is required
                        </p>
                      )}
                    </>
                  )}
                </div>
              );
            })}
            <div className="md:col-span-2 space-y-2">
              <Label
                htmlFor="gst"
                className={`text-base font-medium text-foreground block ${companyFormMode === 'edit' ? 'cursor-default' : 'cursor-pointer'}`}
              >
                GST Registered
                {companyFormMode === 'edit' && (
                  <span className="text-muted-foreground font-normal ml-1">(read-only)</span>
                )}
              </Label>
              <div className="flex items-center gap-3">
                <Checkbox
                  id="gst"
                  checked={addForm.GST}
                  onCheckedChange={(v) => setAddForm((p) => ({ ...p, GST: !!v }))}
                  disabled={companyFormMode === 'edit'}
                  className="h-5 w-5 border-2 data-[state=checked]:border-primary"
                  aria-label="Company is GST registered"
                  aria-readonly={companyFormMode === 'edit'}
                />
                <span className="text-sm text-muted-foreground">
                  {addForm.GST ? 'Yes – company is GST registered' : 'No – company is not GST registered'}
                  {companyFormMode === 'edit' && ' — cannot be changed when editing'}
                </span>
              </div>
            </div>
            {companyFormMode === 'add' && (
              <div className="md:col-span-2 space-y-2">
                <Label>Logo</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Logo path or browse"
                    value={logoPath}
                    onChange={(e) => setLogoPath(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    variant="outline"
                    type="button"
                    onClick={() => document.getElementById('logo-upload-add')?.click()}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Browse
                  </Button>
                  <input
                    id="logo-upload-add"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) {
                        setLogoFile(f);
                        setLogoPath(f.name);
                        setLogoUploadedUrl('');
                        setLogoUploading(true);
                        getImageUrlFromUpload(f)
                          .then((url) => {
                            setLogoUploadedUrl(url);
                          })
                          .catch((err) => {
                            console.warn('[getWebURL] Upload on file select failed:', err);
                            toast.error('Image upload failed. Please try again or try a different image.');
                            setLogoFile(null);
                            setLogoPath('');
                            setLogoUploadedUrl('');
                            e.target.value = '';
                          })
                          .finally(() => setLogoUploading(false));
                      }
                    }}
                  />
                </div>
                {logoUploading && (
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Uploading logo…
                  </p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            {companyFormMode === 'edit' ? (
              <>
                <Button variant="outline" onClick={() => { setAddModalOpen(false); setEditingCompanyId(null); }}>
                  Cancel
                </Button>
                <Button onClick={handleUpdateCompany} disabled={addSubmitting}>
                  {addSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pencil className="h-4 w-4 mr-2" />}
                  Update
                </Button>
              </>
            ) : (
              <>
                <Button variant="secondary" onClick={resetAddForm}>
                  Clear
                </Button>
                <Button onClick={handleAddCompany} disabled={addSubmitting}>
                  {addSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                  Add
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete company confirmation */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete company</DialogTitle>
            <DialogDescription>
              Delete {selectedIds.size} company(ies)? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Modify Image Modal */}
      <Dialog open={imageModalOpen} onOpenChange={setImageModalOpen}>
        <DialogContent className="max-w-md animate-in zoom-in-95 duration-200">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Image className="h-5 w-5" />
              Add / Modify Company Logo
            </DialogTitle>
            <DialogDescription>
              {selectedCompany ? `Updating logo for ${selectedCompany.Name}` : 'Select a company first.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Browse</Label>
              <div className="flex gap-2">
                <Input
                  value={imageForm.path}
                  onChange={(e) => setImageForm((p) => ({ ...p, path: e.target.value }))}
                  placeholder="Image path"
                />
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => document.getElementById('logo-upload-modal')?.click()}
                >
                  <Upload className="h-4 w-4" />
                </Button>
                <input
                  id="logo-upload-modal"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) {
                      setImageForm((p) => ({ ...p, path: f.name, file: f }));
                      setImageModalUploadedUrl('');
                      getImageUrlFromUpload(f)
                        .then((url) => {
                          setImageModalUploadedUrl(url);
                        })
                        .catch((err) => {
                          console.warn('[getWebURL] Add/Modify Logo upload failed:', err);
                          toast.error('Logo upload failed. The image could not be uploaded. Please try another image or try again.');
                          setImageForm((p) => ({ ...p, path: '', file: null }));
                          setImageModalUploadedUrl('');
                          e.target.value = '';
                        });
                    }
                  }}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImageModalOpen(false)}>
              Close
            </Button>
            <Button onClick={handleImageUpload} disabled={imageSubmitting}>
              {imageSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bank Details Modal */}
      <Dialog open={bankModalOpen} onOpenChange={setBankModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Bank Details
            </DialogTitle>
            <DialogDescription>
              {selectedCompany ? `Bank details for ${selectedCompany.Name}` : 'Select a company first.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {[
              { key: 'AccountHolderName', label: 'Account Holder Name' },
              { key: 'BankName', label: 'Name of Bank' },
              { key: 'IFSCCode', label: 'IFSC Code' },
              { key: 'AccountNumber', label: 'Account Number' },
            ].map(({ key, label }) => (
              <div key={key} className="space-y-2">
                <Label>{label}</Label>
                <Input
                  value={(bankForm as any)[key] || ''}
                  onChange={(e) => setBankForm((p) => ({ ...p, [key]: e.target.value }))}
                  placeholder={label}
                />
              </div>
            ))}
            <div className="space-y-2">
              <Label>Bank Address</Label>
              <Textarea
                value={bankForm.BankAddress}
                onChange={(e) => setBankForm((p) => ({ ...p, BankAddress: e.target.value }))}
                placeholder="Bank address"
                rows={4}
                className="resize-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBankModalOpen(false)}>
              Close
            </Button>
            <Button onClick={handleBankSave} disabled={bankSubmitting}>
              {bankSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Terms & Conditions Modal */}
      <Dialog open={termsModalOpen} onOpenChange={setTermsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Terms & Conditions
            </DialogTitle>
            <DialogDescription>
              {selectedCompany ? `Terms for ${selectedCompany.Name}. Select document type to load or edit.` : 'Select a company first.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 space-y-2">
                <Label>Document Type</Label>
                <Select
                  value={termsForm.documentType}
                  onValueChange={handleTermsDocumentTypeChange}
                  disabled={termsLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={termsLoading ? 'Loading...' : 'Select type'} />
                  </SelectTrigger>
                  <SelectContent>
                    {DOCUMENT_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 space-y-2">
                <Label>Header (Print Name)</Label>
                <Input
                  value={termsForm.printName}
                  onChange={(e) => setTermsForm((p) => ({ ...p, printName: e.target.value }))}
                  placeholder="Header / print name"
                  disabled={termsLoading}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Terms & Conditions</Label>
              {termsLoading && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading terms...
                </div>
              )}
              <Textarea
                value={termsForm.content}
                onChange={(e) => setTermsForm((p) => ({ ...p, content: e.target.value }))}
                placeholder="Enter terms and conditions..."
                rows={10}
                className="resize-none font-mono text-sm"
                disabled={termsLoading}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTermsModalOpen(false)}>
              Close
            </Button>
            <Button onClick={handleTermsSave} disabled={termsSubmitting || termsLoading}>
              {termsSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Invoice / Quote / DC Sequence Modal */}
      <Dialog open={sequenceModalOpen} onOpenChange={setSequenceModalOpen}>
        <DialogContent className="w-[95vw] max-w-[560px] max-h-[90vh] overflow-y-auto md:max-h-[85vh] p-4 md:p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Hash className="h-5 w-5" />
              Reset Invoice Sequence
            </DialogTitle>
            <DialogDescription>
              {selectedCompany ? `Sequence for ${selectedCompany.Name}` : 'Select a company first.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <h3 className="text-sm font-medium text-foreground">Reset Sequence Number</h3>
            {sequenceLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="seq-invoice">Last Invoice Number</Label>
                    <Input
                      id="seq-invoice"
                      type="number"
                      min={0}
                      inputMode="numeric"
                      value={sequenceForm.maxInvoiceNumber}
                      onChange={(e) => {
                        const v = e.target.value === '' ? 0 : parseInt(e.target.value, 10);
                        setSequenceForm((p) => ({ ...p, maxInvoiceNumber: isNaN(v) ? 0 : Math.max(0, v) }));
                      }}
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="seq-quote">Last Quote Number</Label>
                    <Input
                      id="seq-quote"
                      type="number"
                      min={0}
                      inputMode="numeric"
                      value={sequenceForm.maxQuoteNumber}
                      onChange={(e) => {
                        const v = e.target.value === '' ? 0 : parseInt(e.target.value, 10);
                        setSequenceForm((p) => ({ ...p, maxQuoteNumber: isNaN(v) ? 0 : Math.max(0, v) }));
                      }}
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="seq-dc">Last DC Number</Label>
                    <Input
                      id="seq-dc"
                      type="number"
                      min={0}
                      inputMode="numeric"
                      value={sequenceForm.maxDCNumber}
                      onChange={(e) => {
                        const v = e.target.value === '' ? 0 : parseInt(e.target.value, 10);
                        setSequenceForm((p) => ({ ...p, maxDCNumber: isNaN(v) ? 0 : Math.max(0, v) }));
                      }}
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="seq-invoice-prefix">Invoice Prefix</Label>
                    <Input
                      id="seq-invoice-prefix"
                      type="text"
                      value={sequenceForm.invoicePrefix}
                      onChange={(e) => setSequenceForm((p) => ({ ...p, invoicePrefix: e.target.value }))}
                      placeholder="e.g. BTC-"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="seq-quote-prefix">Quote Prefix</Label>
                    <Input
                      id="seq-quote-prefix"
                      type="text"
                      value={sequenceForm.quotePrefix}
                      onChange={(e) => setSequenceForm((p) => ({ ...p, quotePrefix: e.target.value }))}
                      placeholder="e.g. q-q-q"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="seq-dc-prefix">DC Prefix</Label>
                    <Input
                      id="seq-dc-prefix"
                      type="text"
                      value={sequenceForm.dcPrefix}
                      onChange={(e) => setSequenceForm((p) => ({ ...p, dcPrefix: e.target.value }))}
                      placeholder="e.g. dc-dc-dc"
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground border-l-2 border-muted pl-3 py-1">
                  If Last Invoice Number = 0, next invoice will start from 1. If Last Invoice Number = 9, next invoice will start from 10. (Same logic for Quote and DC numbers.)
                </p>
              </>
            )}
          </div>
          <DialogFooter className="flex-col-reverse gap-2 sm:flex-row md:gap-2 pt-4 border-t md:pb-0 pb-4 sticky bottom-0 bg-background md:static">
            <Button variant="outline" onClick={() => setSequenceModalOpen(false)} className="w-full sm:w-auto">
              Close
            </Button>
            <Button
              onClick={handleSequenceSave}
              disabled={sequenceSubmitting || sequenceLoading}
              className="w-full sm:w-auto"
            >
              {sequenceSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanyManagementPage;
